import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCorners, useDraggable, useDroppable, useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { SOURCE_LABELS, formatILS, formatRelativeDay } from '@takumi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, GripVertical } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { PageHeader } from '@/components/AppShell';
import { PageLoader } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { keys, useMoveStage } from '@/lib/queries';
import type { OrgSummary, Stage } from '@/lib/types';
import { cn } from '@/lib/utils';
import { FollowupBadge, PriorityDot } from '@/features/crm/badges';
import { LostReasonDialog } from '@/features/organizations/LostReasonDialog';

interface Board {
  stages: Stage[];
  cards: OrgSummary[];
  total: number;
  wonLast30Days: number;
}

export function PipelinePage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: keys.board, queryFn: () => api.get<Board>('/pipeline/board') });
  const move = useMoveStage();
  const [dragging, setDragging] = useState<OrgSummary | null>(null);
  const [pendingLost, setPendingLost] = useState<{ orgId: string; stageId: string } | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));

  if (isLoading || !data) return <PageLoader />;

  const byStage = new Map<string, OrgSummary[]>();
  for (const s of data.stages) byStage.set(s.id, []);
  for (const c of data.cards) byStage.get(c.stageId)?.push(c);

  const onDragStart = (e: DragStartEvent) => setDragging(data.cards.find((c) => c.id === e.active.id) ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const orgId = String(e.active.id);
    const stageId = e.over ? String(e.over.id) : null;
    const card = data.cards.find((c) => c.id === orgId);
    if (!stageId || !card || card.stageId === stageId) return;
    const stage = data.stages.find((s) => s.id === stageId);
    if (stage?.kind === 'lost') {
      setPendingLost({ orgId, stageId });
      return;
    }
    // Optimistic move; the server is the source of truth and the board is refetched.
    qc.setQueryData<Board>(keys.board, (b) =>
      b && { ...b, cards: b.cards.map((c) => (c.id === orgId ? { ...c, stageId, stageName: stage?.name ?? c.stageName, stageColor: stage?.color ?? c.stageColor } : c)) },
    );
    move.mutate({ orgId, stageId });
  };

  const openValue = data.cards.filter((c) => data.stages.find((s) => s.id === c.stageId)?.kind === 'open').reduce((s, c) => s + (c.estimatedValue ?? 0), 0);

  return (
    <div>
      <PageHeader
        title="פייפליין"
        subtitle={`${data.cards.length} לידים פתוחים · שווי משוער ${formatILS(openValue, { decimals: false })} · ${data.wonLast30Days} הפכו ללקוחות ב-30 הימים האחרונים`}
      />
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-4 lg:-mx-8 lg:px-8" style={{ minHeight: 'calc(100vh - 12rem)' }}>
          {data.stages.map((s) => (
            <Column key={s.id} stage={s} cards={byStage.get(s.id) ?? []} canDrag={can('crm.write')} />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>{dragging && <CardView card={dragging} overlay />}</DragOverlay>
      </DndContext>
      <LostReasonDialog
        open={!!pendingLost}
        onOpenChange={(o) => !o && setPendingLost(null)}
        onConfirm={(reason) => {
          if (pendingLost) move.mutate({ ...pendingLost, lostReason: reason });
          setPendingLost(null);
        }}
      />
    </div>
  );
}

function Column({ stage, cards, canDrag }: { stage: Stage; cards: OrgSummary[]; canDrag: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });
  const value = cards.reduce((s, c) => s + (c.estimatedValue ?? 0), 0);
  const closedStage = stage.kind !== 'open';
  return (
    <section
      ref={setNodeRef}
      aria-label={stage.name}
      className={cn(
        'flex w-[272px] shrink-0 flex-col rounded-card border bg-sunken/50 transition-colors',
        isOver ? 'border-matcha-400 bg-matcha-50' : 'border-line',
        closedStage && 'w-[220px] bg-sunken/30',
      )}
    >
      <header className="flex items-center gap-2 px-3 pt-3 pb-2">
        <span className="size-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
        <h2 className="flex-1 truncate text-sm font-bold">{stage.name}</h2>
        {stage.kind === 'won' && <Crown className="size-4 text-gold-500" />}
        <span className="rounded-full bg-surface px-2 text-xs font-bold text-muted tabular">{cards.length}</span>
      </header>
      {stage.kind === 'open' && value > 0 && <div className="px-3 pb-2 text-xs text-muted tabular">{formatILS(value, { decimals: false })}</div>}
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-2 pb-3">
        {cards.map((c) => (
          <DraggableCard key={c.id} card={c} disabled={!canDrag} />
        ))}
        {stage.kind === 'won' && <p className="px-2 py-3 text-center text-xs text-muted">גררו לכאן ליד כדי להפוך אותו ללקוח</p>}
      </div>
    </section>
  );
}

function DraggableCard({ card, disabled }: { card: OrgSummary; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, disabled });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className={cn(isDragging && 'opacity-30')}>
      <CardView card={card} />
    </div>
  );
}

function CardView({ card, overlay }: { card: OrgSummary; overlay?: boolean }) {
  const navigate = useNavigate();
  return (
    <article
      onClick={() => !overlay && navigate(`/org/${card.id}`)}
      className={cn(
        'group cursor-pointer rounded-control border border-line bg-surface p-3 shadow-card transition-shadow hover:shadow-pop',
        overlay && 'rotate-[-1.5deg] cursor-grabbing shadow-pop',
      )}
    >
      <div className="flex items-start gap-1.5">
        <PriorityDot priority={card.priority} />
        <Link to={`/org/${card.id}`} onClick={(e) => e.stopPropagation()} className="min-w-0 flex-1 truncate text-sm leading-tight font-semibold hover:underline">
          {card.name}
        </Link>
        <GripVertical className="size-4 shrink-0 text-faint opacity-0 group-hover:opacity-100" />
      </div>
      <div className="mt-1 truncate text-xs text-muted">{[card.contactName, card.city].filter(Boolean).join(' · ') || '—'}</div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {card.stageKind === 'open' && <FollowupBadge dueAt={card.nextFollowupAt} title={card.nextFollowupTitle} />}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-muted">
        <span>{SOURCE_LABELS[card.source]}</span>
        <span>{card.lastContactAt ? `קשר ${formatRelativeDay(card.lastContactAt, new Date(), false)}` : 'טרם נוצר קשר'}</span>
        {card.estimatedValue ? <span className="font-semibold text-ink-soft tabular">{formatILS(card.estimatedValue, { decimals: false })}</span> : null}
      </div>
    </article>
  );
}
